import { ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEnv } from "~/hooks/use-env";
import type { PosthogBootstrap } from "~/.server/posthog";
import { synchronizePosthogIdentity } from "./posthog-identity";

const IdentityReady = createContext(false);
export const usePosthogIdentityReady = () => useContext(IdentityReady);

type Props = {
  children: ReactNode;
  bootstrap?: PosthogBootstrap;
};

export const PosthogProvider = ({ children, bootstrap }: Props) => {
  const { POSTHOG_API_KEY } = useEnv();
  const identityKey = bootstrap?.authID || bootstrap?.anonymousDistinctID;
  const [synchronizedIdentity, setSynchronizedIdentity] = useState<string>();
  const initializedKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!POSTHOG_API_KEY) return;

    let synchronizedDuringInitialization = false;
    if (initializedKey.current !== POSTHOG_API_KEY) {
      posthog.init(POSTHOG_API_KEY, {
        api_host: "/ph-relay",
        ui_host: "https://us.posthog.com",
        loaded: client => {
          // Read persisted ownership before changing identity, and synchronize
          // before the SDK captures its initial pageview.
          if (bootstrap) synchronizePosthogIdentity(client, bootstrap);
          synchronizedDuringInitialization = true;
        },
      });
      initializedKey.current = POSTHOG_API_KEY;
    }

    // init is a no-op when the singleton survives a provider remount.
    if (bootstrap && !synchronizedDuringInitialization) {
      synchronizePosthogIdentity(posthog, bootstrap);
    }

    setSynchronizedIdentity(identityKey);
  }, [POSTHOG_API_KEY, bootstrap]);

  // Keep the subtree mounted across hydration and identity revalidation.
  return <PostHogProvider client={posthog}><IdentityReady.Provider value={!!POSTHOG_API_KEY && !!identityKey && synchronizedIdentity === identityKey}>{children}</IdentityReady.Provider></PostHogProvider>;
};
